import { Button, Flex, Skeleton, Typography } from "antd"
import type { ColumnType } from "antd/es/table/interface"
import React from "react"
import { useTranslation } from "react-i18next"

import { ProjectStatusTag } from "../../../Project/components/ProjectStatusTag.tsx"
import { isArchivingStatus } from "../../constants/project-statuses.ts"
import type { ProjectListItem } from "../../types/types.ts"
import {
  NextMilestoneCell,
  ProgressCell,
  ProjectIdentityCell,
  ProjectManagerCell,
  ScheduleHealthCell,
  compactCurrency
} from "./project-cells.tsx"

const { Text } = Typography

interface ProjectMobileListProps {
  projects: ProjectListItem[]
  loading: boolean
  hasMore: boolean
  showMilestones: boolean
  showProgress: boolean
  actionsColumn: ColumnType<ProjectListItem>
  empty: React.ReactNode
  onLoadMore: () => void
}

function Field({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <Flex vertical gap={2} className="min-w-0">
      <Text className="text-xs text-slate-500">{label}</Text>
      {children}
    </Flex>
  )
}

/** The exceptions a card must not lose when the Financial and Last activity
 *  columns are not there to carry them. */
function ProjectExceptions({ project }: { project: ProjectListItem }) {
  const { t } = useTranslation()
  const overBudget = project.financial_exception === "over_budget"
  // A closed project is not waiting on anyone, however quiet it has been.
  const inactive =
    !!project.activity_stale && !isArchivingStatus(project.project_status)
  if (!overBudget && !inactive) return null
  return (
    <Flex gap={8} wrap="wrap" className="mt-2">
      {overBudget ? (
        <span className="rounded bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
          {project.over_budget_amount
            ? t("projectsCommand.financial.overBudgetBy", {
                amount: compactCurrency(project.over_budget_amount)
              })
            : t("projectsCommand.financial.overBudget")}
        </span>
      ) : null}
      {inactive ? (
        <span className="rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
          {t("projectsCommand.activity.inactive")}
        </span>
      ) : null}
    </Flex>
  )
}

/** Below the table's comfortable width a row becomes a card rather than a
 *  squeezed table: identity first, then the fields that answer "does this
 *  need me". Financial is left to the project page at this size. */
export function ProjectMobileList({
  projects,
  loading,
  hasMore,
  showMilestones,
  showProgress,
  actionsColumn,
  empty,
  onLoadMore
}: ProjectMobileListProps) {
  const { t } = useTranslation()

  if (loading && !projects.length) {
    return (
      <Flex vertical gap={12} className="py-2" aria-busy>
        {[0, 1, 2].map((key) => (
          <Skeleton key={key} active paragraph={{ rows: 3 }} />
        ))}
      </Flex>
    )
  }
  if (!projects.length) return <div className="py-2">{empty}</div>

  return (
    <Flex vertical gap={12} className="py-2">
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {projects.map((project, index) => (
          <li
            key={project.id}
            className="rounded-lg border border-solid border-slate-200 bg-white p-4"
          >
            <Flex justify="space-between" gap={8} align="start">
              <div className="min-w-0 flex-1">
                <ProjectIdentityCell project={project} />
              </div>
              {
                actionsColumn.render?.(
                  undefined,
                  project,
                  index
                ) as React.ReactNode
              }
            </Flex>
            <ProjectExceptions project={project} />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label={t("projectsCommand.columns.status")}>
                <div>
                  <ProjectStatusTag status={project.project_status} />
                </div>
              </Field>
              <Field label={t("projectsCommand.columns.schedule")}>
                <ScheduleHealthCell project={project} />
              </Field>
              {showMilestones ? (
                <Field label={t("projectsCommand.columns.nextMilestone")}>
                  <NextMilestoneCell project={project} />
                </Field>
              ) : null}
              <Field label={t("projectsCommand.columns.projectManager")}>
                <ProjectManagerCell project={project} />
              </Field>
              {showProgress ? (
                <div className="col-span-2">
                  <Field label={t("projectsCommand.columns.progress")}>
                    <ProgressCell project={project} />
                  </Field>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {hasMore ? (
        <Button onClick={onLoadMore} loading={loading} block>
          {t("projectsCommand.loadMore")}
        </Button>
      ) : null}
    </Flex>
  )
}
